import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Hash, Shield, Users, Briefcase, Calculator, CheckCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import useSEO from '../hooks/useSEO';

const UitlegPage = () => {
  useSEO(); // Apply SEO
  
  const { t, language } = useLanguage();

  // Tax type pages for internal linking (SEO)
  const taxTypePages = [
    { slug: 'omzetbelasting', name: 'Omzetbelasting (BTW)', code: 'B/F' },
    { slug: 'loonheffing', name: 'Loonheffing', code: 'L/A' },
    { slug: 'inkomstenbelasting', name: 'Inkomstenbelasting', code: 'H' },
    { slug: 'vennootschapsbelasting', name: 'Vennootschapsbelasting', code: 'V' },
    { slug: 'motorrijtuigenbelasting', name: 'Motorrijtuigenbelasting', code: 'M/Y' },
    { slug: 'toeslagen', name: 'Toeslagen', code: 'T' },
    { slug: 'zorgverzekeringswet', name: 'Zorgverzekeringswet', code: 'W' },
  ];

  // Correct B-MIDDEL codes according to official Belastingdienst specification
  const taxTypes = [
    { code: '0X', name: language === 'nl' ? 'Loonheffing - Naheffing' : 'Wage Tax - Additional' },
    { code: '1X', name: language === 'nl' ? 'Omzetbelasting (BTW)' : 'VAT' },
    { code: '5X', name: language === 'nl' ? 'Omzetbelasting - Naheffing' : 'VAT - Additional' },
    { code: '6X', name: language === 'nl' ? 'Loonheffing' : 'Wage Tax' },
    { code: '23', name: language === 'nl' ? 'Kinderopvangtoeslag' : 'Childcare Benefit' },
    { code: '24', name: language === 'nl' ? 'Huurtoeslag' : 'Housing Benefit' },
    { code: '25', name: language === 'nl' ? 'Zorgtoeslag' : 'Healthcare Benefit' },
    { code: '26', name: language === 'nl' ? 'Kindgebonden budget' : 'Child Budget' },
    { code: '70', name: language === 'nl' ? 'Inkomstenbelasting' : 'Income Tax' },
    { code: '74', name: language === 'nl' ? 'Vennootschapsbelasting' : 'Corporate Tax' },
    { code: '75', name: language === 'nl' ? 'Zorgverzekeringswet' : 'Health Insurance Act' },
    { code: '78', name: language === 'nl' ? 'Houderschapsbelasting' : 'Vehicle Tax' },
    { code: '85', name: language === 'nl' ? 'Eurovignet' : 'Eurovignette' },
    { code: '87', name: language === 'nl' ? 'MOA (vrachtwagens)' : 'Heavy Vehicle Tax' },
    { code: '97', name: language === 'nl' ? 'Landinrichtingsrente' : 'Land Consolidation' },
  ];

  // TYDVAK period codes for LB/OB
  const periodCodes = [
    { code: '01-12', name: language === 'nl' ? 'Maand 1-12' : 'Month 1-12' },
    { code: '21', name: language === 'nl' ? '1e kwartaal (Q1)' : 'Quarter 1 (Q1)' },
    { code: '24', name: language === 'nl' ? '2e kwartaal (Q2)' : 'Quarter 2 (Q2)' },
    { code: '27', name: language === 'nl' ? '3e kwartaal (Q3)' : 'Quarter 3 (Q3)' },
    { code: '30', name: language === 'nl' ? '4e kwartaal (Q4)' : 'Quarter 4 (Q4)' },
    { code: '40/50', name: language === 'nl' ? 'Jaaraangifte' : 'Annual Return' },
  ];

  const audiences = [
    { icon: Briefcase, text: t('uitleg_who_1') },
    { icon: Users, text: t('uitleg_who_2') },
    { icon: Calculator, text: t('uitleg_who_3') },
    { icon: CheckCircle, text: t('uitleg_who_4') },
  ];

  return (
    <div className="py-12 md:py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-6">
            <BookOpen className="w-8 h-8 text-accent" />
          </div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-primary dark:text-white mb-4">
            {t('uitleg_title')}
          </h1>
          <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed">
            {t('uitleg_intro')}
          </p>
        </motion.div>

        {/* Structure explanation */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-16"
        >
          <h2 className="font-heading font-bold text-2xl text-primary dark:text-white mb-6 flex items-center gap-3">
            <Hash className="w-6 h-6 text-accent" />
            {t('uitleg_structure_title')}
          </h2>
          
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            {/* Visual representation - Example: 4865527461501300 */}
            <div className="p-6 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-3 text-center">
                {language === 'nl' ? 'Voorbeeld: BTW Q4 2025' : 'Example: VAT Q4 2025'}
              </p>
              <div className="font-mono text-xl md:text-2xl tracking-widest text-center flex flex-wrap justify-center gap-1">
                <span className="px-3 py-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded font-bold">4</span>
                <span className="px-3 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded">8655274</span>
                <span className="px-2 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded">6</span>
                <span className="px-3 py-2 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded font-bold">1</span>
                <span className="px-3 py-2 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 rounded font-bold">5</span>
                <span className="px-3 py-2 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded">01</span>
                <span className="px-3 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded font-bold">30</span>
                <span className="px-3 py-2 bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded">0</span>
              </div>
            </div>
            
            {/* Legend */}
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-4">
                <span className="shrink-0 w-10 h-8 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-sm font-bold text-red-600">1</span>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">
                    {language === 'nl' ? 'Controlecijfer (mod-11 check)' : 'Check digit (mod-11 check)'}
                  </p>
                  <p className="text-sm text-slate-500">{language === 'nl' ? 'Valideert het kenmerk tegen tikfouten' : 'Validates the reference against typos'}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="shrink-0 w-10 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-sm font-bold text-blue-600">2-9</span>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">
                    {language === 'nl' ? 'BSN / RSIN (fiscaal nummer)' : 'BSN / RSIN (fiscal number)'}
                  </p>
                  <p className="text-sm text-slate-500">{language === 'nl' ? 'Eerste 8 cijfers van het fiscaal nummer' : 'First 8 digits of fiscal number'}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="shrink-0 w-10 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-sm font-bold text-green-600">10</span>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">
                    {language === 'nl' ? 'Belastingtype indicator' : 'Tax type indicator'}
                  </p>
                  <p className="text-sm text-slate-500">{language === 'nl' ? '0=LH-nah, 1=BTW, 5=BTW-nah, 6=LH' : '0=WT-add, 1=VAT, 5=VAT-add, 6=WT'}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="shrink-0 w-10 h-8 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center text-sm font-bold text-yellow-600">11</span>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">
                    {language === 'nl' ? 'Jaar (laatste cijfer)' : 'Year (last digit)'}
                  </p>
                  <p className="text-sm text-slate-500">{language === 'nl' ? 'Bijv. 5 = 2025, 3 = 2023' : 'E.g. 5 = 2025, 3 = 2023'}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="shrink-0 w-10 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-sm font-bold text-orange-600">12-13</span>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">
                    {language === 'nl' ? 'Subnummer' : 'Sub number'}
                  </p>
                  <p className="text-sm text-slate-500">{language === 'nl' ? 'Intern volgnummer' : 'Internal sequence number'}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="shrink-0 w-10 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-sm font-bold text-purple-600">14-15</span>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">
                    {language === 'nl' ? 'Tijdvak (periode)' : 'Period'}
                  </p>
                  <p className="text-sm text-slate-500">{language === 'nl' ? '01-12=maand, 21/24/27/30=kwartaal' : '01-12=month, 21/24/27/30=quarter'}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="shrink-0 w-10 h-8 rounded-lg bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-sm font-bold text-slate-600">16</span>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">
                    {language === 'nl' ? 'Volgnummer' : 'Sequence number'}
                  </p>
                  <p className="text-sm text-slate-500">{language === 'nl' ? 'Extra identificatie' : 'Additional identification'}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Note about structure */}
          <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>{language === 'nl' ? 'Let op:' : 'Note:'}</strong> {language === 'nl' 
                ? 'De structuur verschilt per belastingtype. Bovenstaand voorbeeld geldt voor Loonheffing (LH) en Omzetbelasting (OB). Andere belastingen zoals Inkomstenbelasting en Vennootschapsbelasting hebben een andere indeling.'
                : 'The structure varies by tax type. The example above applies to Wage Tax and VAT. Other taxes like Income Tax and Corporate Tax have different structures.'}
            </p>
          </div>
        </motion.section>

        {/* Tax types */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-16"
        >
          <h2 className="font-heading font-bold text-2xl text-primary dark:text-white mb-6">
            {language === 'nl' ? 'B-MIDDEL Codes (positie 10-11)' : 'B-MIDDEL Codes (position 10-11)'}
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-4 text-sm">
            {language === 'nl' 
              ? 'Voor LH/OB: positie 10 is type (0/1/5/6), positie 11 is jaarcijfer. Voor andere belastingen: beide posities samen vormen de code.'
              : 'For WT/VAT: position 10 is type (0/1/5/6), position 11 is year digit. For other taxes: both positions together form the code.'}
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {taxTypes.map(({ code, name }) => (
              <div
                key={code}
                className="flex items-center gap-4 p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700"
              >
                <span className="shrink-0 w-12 h-10 rounded-lg bg-accent/10 flex items-center justify-center font-mono font-bold text-accent text-sm">
                  {code}
                </span>
                <span className="text-slate-700 dark:text-slate-200 text-sm">{name}</span>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Period codes */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mb-16"
        >
          <h2 className="font-heading font-bold text-2xl text-primary dark:text-white mb-6">
            {language === 'nl' ? 'Tijdvak Codes (positie 14-15)' : 'Period Codes (position 14-15)'}
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-4 text-sm">
            {language === 'nl' 
              ? 'Voor LH/OB aangiftes bepaalt dit de periode van de aangifte.'
              : 'For WT/VAT returns, this determines the period of the return.'}
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {periodCodes.map(({ code, name }) => (
              <div
                key={code}
                className="flex items-center gap-4 p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700"
              >
                <span className="shrink-0 w-12 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center font-mono font-bold text-purple-600 dark:text-purple-400 text-sm">
                  {code}
                </span>
                <span className="text-slate-700 dark:text-slate-200 text-sm">{name}</span>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Mod-11 validation */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-16"
        >
          <h2 className="font-heading font-bold text-2xl text-primary dark:text-white mb-6 flex items-center gap-3">
            <Shield className="w-6 h-6 text-accent" />
            {t('uitleg_validation_title')}
          </h2>
          
          <div className="p-6 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700">
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
              {t('uitleg_validation_text')}
            </p>
            <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg font-mono text-sm">
              <p className="text-slate-500 mb-2">{language === 'nl' ? 'Formule:' : 'Formula:'}</p>
              <p className="text-slate-900 dark:text-white">
                (d₁×2 + d₂×4 + d₃×8 + ... + d₁₆×9) mod 11 = 0
              </p>
            </div>
          </div>
        </motion.section>

        {/* Who is this for */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="font-heading font-bold text-2xl text-primary dark:text-white mb-6">
            {t('uitleg_who_title')}
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {audiences.map(({ icon: Icon, text }, idx) => (
              <div
                key={idx}
                className="flex items-center gap-4 p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700"
              >
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-accent" />
                </div>
                <span className="font-medium text-slate-700 dark:text-slate-200">{text}</span>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Internal Links to Tax Type Pages - SEO */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-16"
        >
          <h2 className="font-heading font-bold text-2xl text-primary dark:text-white mb-6">
            {language === 'nl' ? 'Meer informatie per belastingtype' : 'More information per tax type'}
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {taxTypePages.map(({ slug, name, code }) => (
              <Link
                key={slug}
                to={`/belastingtype/${slug}`}
                className="group flex items-center justify-between p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-accent hover:shadow-lg transition-all"
              >
                <div>
                  <span className="font-medium text-slate-700 dark:text-slate-200 group-hover:text-accent transition-colors">
                    {name}
                  </span>
                  <span className="block text-sm text-slate-500 font-mono">Code: {code}</span>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-accent group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default UitlegPage;
